import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage, defineMessages } from 'react-intl';

const messages = defineMessages({
    placeholder: {
        id: 'overdueBills.detail.placeholder',
        defaultMessage: 'Select an invoice on the left to see details'
    },
    clearSelection: {
        id: 'overdueBills.detail.clearSelection',
        defaultMessage: 'Clear selection'
    },
    title: {
        id: 'overdueBills.detail.title',
        defaultMessage: 'Invoice detail'
    },
    dueDate: {
        id: 'overdueBills.detail.dueDate',
        defaultMessage: 'Due date'
    },
    amountToPay: {
        id: 'overdueBills.detail.amountToPay',
        defaultMessage: 'Amount to pay'
    },
    logs: {
        id: 'overdueBills.detail.logs',
        defaultMessage: 'Logs'
    },
    communications: {
        id: 'overdueBills.detail.communications',
        defaultMessage: 'Communications'
    },
    logTime: {
        id: 'overdueBills.detail.logs.time',
        defaultMessage: 'Time'
    },
    logType: {
        id: 'overdueBills.detail.logs.type',
        defaultMessage: 'Type'
    },
    logTitle: {
        id: 'overdueBills.detail.logs.title',
        defaultMessage: 'Title'
    },
    logMessage: {
        id: 'overdueBills.detail.logs.message',
        defaultMessage: 'Message'
    },
    commTime: {
        id: 'overdueBills.detail.comms.time',
        defaultMessage: 'Time'
    },
    commType: {
        id: 'overdueBills.detail.comms.type',
        defaultMessage: 'Type'
    },
    commRecipient: {
        id: 'overdueBills.detail.comms.recipient',
        defaultMessage: 'Recipient'
    },
    commMessage: {
        id: 'overdueBills.detail.comms.message',
        defaultMessage: 'Message'
    }
});

export default class OverdueBillDetail extends React.Component {
    static propTypes = {
        bill: PropTypes.object,
        canSeeCommunications: PropTypes.bool,
        isDeveloper: PropTypes.bool,
        onClear: PropTypes.func,
    };
    render() {
        const { bill } = this.props; // {id, code}
        if (!bill) return (
            <div className="panel panel-default">
                <div className="panel-heading">
                    <h3 className="panel-title"><FormattedMessage {...messages.title} /></h3>
                </div>
                <div className="panel-body text-muted" style={{ textAlign: 'center', padding: 16 }}>
                    <i className="fa fa-hand-o-left" style={{ marginRight: 6 }}></i>
                    <FormattedMessage {...messages.placeholder} />
                </div>
            </div>
        );
        const logs = bill.logs || [];
        const comms = bill.communications || [];
        const toText = (v) => {
            if (v == null) return '';
            if (typeof v === 'object') return JSON.stringify(v);
            return String(v);
        };
        return (
            <div className="panel panel-default overdue-bill-detail">
                <div className="panel-heading clearfix">
                    <h3 className="panel-title" style={{ display: 'inline-block' }}>
                        <FormattedMessage {...messages.title} /> {toText(bill.code)}
                    </h3>
                    <button className="btn btn-default btn-xs pull-right" onClick={() => this.props.onClear && this.props.onClear()} title="Clear selection">
                        <i className="fa fa-times" style={{ marginRight: 4 }}></i>
                        <FormattedMessage {...messages.clearSelection} />
                    </button>
                </div>
                <div className="panel-body">
                    <div>
                        <strong><FormattedMessage {...messages.dueDate} />:</strong> {bill.dueDate ? moment(bill.dueDate).format('DD.MM.YYYY') : ''}
                    </div>
                    <div>
                        <strong><FormattedMessage {...messages.amountToPay} />:</strong> {toText(bill.amountToPay)}
                    </div>
                    <hr/>
                    {this.props.isDeveloper ? (
                        <div style={{ marginBottom: 16 }}>
                            <h4><FormattedMessage {...messages.logs} /></h4>
                            <table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th><FormattedMessage {...messages.logTime} /></th>
                                        <th><FormattedMessage {...messages.logType} /></th>
                                        <th><FormattedMessage {...messages.logTitle} /></th>
                                        <th><FormattedMessage {...messages.logMessage} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(l => (
                                        <tr key={l.id}>
                                            <td>{l.timestamp ? moment(l.timestamp).format('DD.MM.YYYY HH:mm') : ''}</td>
                                            <td>{toText(l.type)}</td>
                                            <td>{toText(l.title) || ''}</td>
                                            <td>{toText(l.message) || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                    {this.props.canSeeCommunications ? (
                        <div>
                            <h4><FormattedMessage {...messages.communications} /></h4>
                            <table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th><FormattedMessage {...messages.commTime} /></th>
                                        <th><FormattedMessage {...messages.commType} /></th>
                                        <th><FormattedMessage {...messages.commRecipient} /></th>
                                        <th><FormattedMessage {...messages.commMessage} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comms.map(c => {
                                        const rawMsg = toText(c.message) || '';
                                        // Remove duplicate recipient info (To:/Cc:) from message to avoid duplication with Recipient column
                                        // Remove "To: ..." up to next marker (Cc:/Rule:) or end, then remove "Cc: ..." similarly
                                        let cleaned = rawMsg.replace(/\s*To:\s*.*?(?=(?:\s+Cc:|\s+Rule:|$))/g, '');
                                        cleaned = cleaned.replace(/\s*Cc:\s*.*?(?=(?:\s+Rule:|$))/g, '');
                                        // Normalize whitespace
                                        cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
                                        return (
                                            <tr key={c.id}>
                                                <td>{c.timestamp ? moment(c.timestamp).format('DD.MM.YYYY HH:mm') : ''}</td>
                                                <td>{toText(c.type)}</td>
                                                <td>{toText(c.recipient) || ''}</td>
                                                <td>{cleaned}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}


